/* -----------------------------
    BASE DIALOG CLASS
   ----------------------------- */

class BaseDialog extends HTMLElement {
  constructor() {
    super();

    this.onTriggerClickListener = this.onTriggerClick.bind(this);
  }

  get selectors() {
    return {
      content: "[data-scrollable-content]"
    }
  };

  get modifiers() {
    return {
      open: "open"
    }
  };

  static get observedAttributes() {
    return ["id", "open"];
  }

  get open() {
    return this.hasAttribute("open");
  }

  get shouldLock() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get shouldTransitionDelay() {
    return true;
  }

  get transitionDelay() {
    return 300;
  }

  get scrollableElement() {
    return this.querySelector(this.selectors.content);
  }

  connectedCallback() {
    if (this.id) {
      document.addEventListener("click", this.onTriggerClickListener);
    }
  }

  disconnectedCallback() {
    if (this.id) {
      document.removeEventListener("click", this.onTriggerClickListener);
    }

    if (this.shouldLock && this.open) {
      themeVendor?.BodyScrollLock?.enableBodyScroll(this.scrollableElement);
    }
  }

  show() {
    if (this.open) {
      return;
    }

    this.setAttribute("open", "");

    this.shouldTransitionDelay
      ? setTimeout(() => this.classList.add(this.modifiers.open))
      : this.classList.add(this.modifiers.open);

    this.dispatchEvent(new CustomEvent("dialog:show"));
  }

  hide() {
    if (!this.open) {
      return;
    }

    this.shouldTransitionDelay
      ? setTimeout(() => this.removeAttribute("open"), this.transitionDelay)
      : this.removeAttribute("open");

    this.dispatchEvent(new CustomEvent("dialog:hide"));
  }

  onTriggerClick(event) {
    const trigger = event.target.closest(`[aria-controls="${this.id}"]`);

    if (!trigger) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.open) {
      this.hide();
    } else {
      this.show();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "open": {
        if (oldValue === null && (newValue === "")) {
          this.originalParentBeforeAppendToBody = null;

          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this.originalParentBeforeAppendToBody = this.parentElement;
            document.body.append(this);
          }

          if (this.shouldLock) {
            themeVendor?.BodyScrollLock?.disableBodyScroll(this.scrollableElement);
          }
        } else if (oldValue !== null && newValue === null) {
          if (this.shouldLock) {
            themeVendor?.BodyScrollLock?.enableBodyScroll(this.scrollableElement);
          }

          if (this.shouldAppendToBody && this.parentElement === document.body) {
            this.originalParentBeforeAppendToBody.appendChild(this);
            this.originalParentBeforeAppendToBody = null;
          }
        }

        break;
      }
    }
  }
}

/* ----------------------------- */

/* -----------------------------
    POPUP
   ----------------------------- */

class BasePopup extends BaseDialog {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}

if (!customElements.get("base-popup")) {
  customElements.define("base-popup", BasePopup);
}

/* ----------------------------- */

/* -----------------------------
    DRAWER
   ----------------------------- */

class BaseDrawer extends BaseDialog {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }
}

if (!customElements.get("base-drawer")) {
  customElements.define("base-drawer", BaseDrawer);
}

/* ----------------------------- */

/* -----------------------------
    FILTER DRAWER
   ----------------------------- */

class FilterDrawer extends BaseDrawer {
  constructor() {
    super();

    this.onDrawerClosedListener = this.submitFilterForm.bind(this);
  }

  get selectors() {
    return {
      ...super.selectors,
      filterForm: "form"
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.filterFormElement = this.querySelector(this.selectors.filterForm);

    this.addEventListener("dialog:hide", this.onDrawerClosedListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("dialog:hide", this.onDrawerClosedListener);
  }

  submitFilterForm() {
    this.filterFormElement.dispatchEvent(new Event("submit"));
  }
}

if (!window.customElements.get("filter-drawer")) {
  window.customElements.define("filter-drawer", FilterDrawer);
}

/* ----------------------------- */

/* -----------------------------
    GIFT WITH PURCHASE DRAWER
   ----------------------------- */

class GiftWithPurchaseDrawer extends BaseDrawer {
  get shouldTransitionDelay() {
    return false;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("gift-with-purchase:add", () => {
      this.hide();
    });
  }
}

if (!window.customElements.get("gift-with-purchase-drawer")) {
  window.customElements.define("gift-with-purchase-drawer", GiftWithPurchaseDrawer);
}

/* ----------------------------- */
